import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/services/http-client.factory';
import type {
	BingoBoardResponseDto,
	BingoGlobalLeaderboardParamsDto,
	BingoGlobalLeaderboardResponseDto,
	BingoGlobalRankParamsDto,
	BingoGlobalRankResponseDto,
	BingoMatchDetailsDto,
	BingoMatchDto,
	BingoMatchLeaderboardParamsDto,
	BingoMatchLeaderboardResponseDto,
	BingoMatchMarketsResponseDto,
	BingoParticipatedMatchDto,
	GetBingoBoardDto,
	GetBingoMatchDetailsDto,
	GetBingoMatchesDto,
	GetBingoMatchMarketsDto,
	GetTodaysParticipatedMatchesDto,
} from '@sportspredict/shared';

export const bingoApi = createApi({
	reducerPath: 'bingoApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: [
		'BingoMatches',
		'BingoBoard',
		'BingoMatchDetails',
		'BingoMatchLeaderboard',
		'BingoGlobalLeaderboard',
		'BingoMatchMarkets',
	],
	endpoints: (builder) => ({
		getUserParticipatedMatches: builder.query<
			BingoParticipatedMatchDto[],
			GetTodaysParticipatedMatchesDto
		>({
			query: (body) => ({
				url: 'bingo/my-games',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { lobbyId }) => [{ type: 'BingoMatches', id: lobbyId }],
		}),
		getBingoMatches: builder.query<BingoMatchDto[], GetBingoMatchesDto>({
			query: (body) => ({
				url: 'bingo/matches',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { lobbyId }) => [{ type: 'BingoMatches', id: lobbyId }],
		}),
		getBingoPublicMatches: builder.query<BingoMatchDto[], GetBingoMatchesDto>({
			query: (body) => ({
				url: 'bingo/public-matches',
				method: 'POST',
				body,
			}),
			extraOptions: { skipAuth: true },
			providesTags: (_result, _error, { lobbyId }) => [{ type: 'BingoMatches', id: lobbyId }],
		}),
		getBingoMatchDetails: builder.query<BingoMatchDetailsDto, GetBingoMatchDetailsDto>({
			query: (body) => ({
				url: 'bingo/match',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { matchId }) => [{ type: 'BingoMatchDetails', id: matchId }],
		}),
		getBingoMatchMarkets: builder.query<BingoMatchMarketsResponseDto, GetBingoMatchMarketsDto>({
			query: (body) => ({
				url: 'bingo/match-markets',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { matchId }) => [{ type: 'BingoMatchMarkets', id: matchId }],
		}),
		/** The user's board for a match, or null until all 25 picks are locked in. */
		getBingoBoard: builder.query<BingoBoardResponseDto | null, GetBingoBoardDto>({
			query: (body) => ({
				url: 'bingo/board',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { matchId, lobbyId }) => [
				{ type: 'BingoBoard', id: `${lobbyId}:${matchId}` },
			],
		}),
		getBingoMatchLeaderboard: builder.query<
			BingoMatchLeaderboardResponseDto,
			BingoMatchLeaderboardParamsDto
		>({
			query: (body) => ({
				url: 'bingo/leaderboard',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { matchId, lobbyId }) => [
				{ type: 'BingoMatchLeaderboard', id: `${matchId}:${lobbyId}` },
			],
		}),
		getBingoGlobalLeaderboard: builder.query<
			BingoGlobalLeaderboardResponseDto,
			BingoGlobalLeaderboardParamsDto
		>({
			query: (body) => ({
				url: 'bingo/global-leaderboard',
				method: 'POST',
				body,
			}),
			providesTags: (_result, _error, { lobbyId }) => [
				{ type: 'BingoGlobalLeaderboard', id: lobbyId },
			],
		}),
		/** The signed-in player's rank and points in the global standings (Standings tab). */
		getBingoGlobalRank: builder.query<BingoGlobalRankResponseDto, BingoGlobalRankParamsDto>({
			query: (body) => ({
				url: 'bingo/global-rank',
				method: 'POST',
				body,
			}),
			// Shares the standings tag so both refresh together.
			providesTags: (_result, _error, { lobbyId }) => [
				{ type: 'BingoGlobalLeaderboard', id: lobbyId },
			],
		}),
	}),
});

export const {
	useGetUserParticipatedMatchesQuery,
	useLazyGetUserParticipatedMatchesQuery,
	useGetBingoMatchesQuery,
	useGetBingoPublicMatchesQuery,
	useGetBingoBoardQuery,
	useGetBingoMatchDetailsQuery,
	useGetBingoMatchLeaderboardQuery,
	useGetBingoGlobalLeaderboardQuery,
	useGetBingoMatchMarketsQuery,
	useGetBingoGlobalRankQuery,
} = bingoApi;
